<script setup lang="ts">
/**
 * `performance-detail` zone: one performance, large.
 *
 * Three template variants, one set of facts. `poster` is a full-bleed image with
 * the text over it, `split` puts the image beside the text, `text` drops the
 * image entirely (a narrow door panel, or a production with no imagery). All
 * three read the same theme tokens, so per-venue typography is a token pass
 * rather than a fork (D5).
 *
 * The empty state is a deliberate render, not a blank: a stage-door panel with
 * nothing on tonight should say so rather than look broken.
 */
import { computed } from 'vue';
import type {
  PerformanceDetailZoneParams,
  ProgrammePayload,
  ProgrammePerformance,
} from './programme-types';
import {
  DEFAULT_PROGRAMME_STRINGS,
  fillTemplate,
  formatLanguages,
  formatTime,
  isInProgress,
  scopedPerformances,
  selectPerformance,
  sponsorLogoUrl,
  surtitleBadges,
  type ProgrammeStrings,
} from './programme';
import QrCode from './QrCode.vue';

const props = defineProps<{
  params: PerformanceDetailZoneParams;
  payload: ProgrammePayload;
  /** Injected so selection is deterministic and testable at any instant. */
  now: Date;
  strings?: ProgrammeStrings | undefined;
}>();

const strings = computed<ProgrammeStrings>(() => props.strings ?? DEFAULT_PROGRAMME_STRINGS);

const performance = computed<ProgrammePerformance | undefined>(() =>
  selectPerformance(
    scopedPerformances(props.payload, props.params),
    props.payload,
    props.now,
    props.params.selection,
  ),
);

const running = computed(() =>
  performance.value ? isInProgress(performance.value, props.now) : false,
);

const posterUrl = computed<string | undefined>(() => {
  const production = performance.value?.production;
  if (!production) return undefined;
  return (
    production.posterUrl ??
    production.images.find((i) => i.role === 'poster')?.url ??
    production.images.find((i) => i.role === 'hero')?.url ??
    production.images[0]?.url
  );
});

const showPoster = computed(
  () => props.params.templateVariant !== 'text' && posterUrl.value !== undefined,
);

/** Location is a fact worth showing whenever the zone is not tied to one stage. */
const showLocation = computed(() => props.params.scope !== 'screen');

const statusLabel = computed<string | null>(() => {
  const status = performance.value?.status;
  if (status === 'cancelled') return strings.value.cancelled;
  if (status === 'postponed') return strings.value.postponed;
  return null;
});

const runtimeLabel = computed<string | null>(() => {
  const minutes = performance.value?.production.runtimeMinutes;
  return minutes ? fillTemplate(strings.value.runtime, { minutes }) : null;
});

const intermissionLabel = computed<string | null>(() => {
  const count = performance.value?.intermissionCount;
  return count && count > 0 ? fillTemplate(strings.value.intermissions, { count }) : null;
});

/**
 * One badge per surtitled language, not one sentence: a visitor looks for their
 * own language on a panel they walk past, and a badge is what the eye finds.
 * The sentence form survives as the group's accessible name.
 */
const surtitles = computed(() => {
  if (!props.params.showSurtitles) return [];
  return surtitleBadges(performance.value?.surtitleLanguages ?? [], props.payload.locale);
});

const surtitleTitle = computed<string>(() =>
  fillTemplate(strings.value.surtitles, {
    languages: formatLanguages(performance.value?.surtitleLanguages ?? [], props.payload.locale),
  }),
);

const cast = computed(() =>
  props.params.showCast ? (performance.value?.production.cast ?? []).slice(0, 8) : [],
);

const sponsors = computed(() =>
  props.params.showSponsors ? (performance.value?.production.sponsors ?? []) : [],
);

function logo(sponsorId: string): string | undefined {
  const sponsor = sponsors.value.find((s) => s.id === sponsorId);
  return sponsor ? sponsorLogoUrl(sponsor, false) : undefined;
}

/**
 * The codes to offer, in the order a visitor wants them: buy a ticket first,
 * then the surtitle app for the performance about to start. Each is present only
 * when the zone asks for it *and* the programme actually carries the URL — an
 * empty plate would be a worse answer than no plate.
 */
const codes = computed<Array<{ key: string; value: string; caption: string }>>(() => {
  const out: Array<{ key: string; value: string; caption: string }> = [];
  const ticketUrl = performance.value?.production.ticketUrl;
  if (props.params.showTicketQr && ticketUrl) {
    out.push({ key: 'ticket', value: ticketUrl, caption: strings.value.ticketsQr });
  }
  const surtitleAppUrl = performance.value?.surtitleAppUrl;
  if (props.params.showSurtitleQr && surtitleAppUrl) {
    out.push({ key: 'surtitle', value: surtitleAppUrl, caption: strings.value.surtitleAppQr });
  }
  return out;
});
</script>

<template>
  <div
    class="lr42-detail"
    :class="[`lr42-detail--${params.templateVariant}`, { 'is-cancelled': statusLabel !== null }]"
  >
    <template v-if="performance">
      <div v-if="showPoster" class="lr42-detail__poster">
        <img :src="posterUrl" alt="" />
      </div>

      <div class="lr42-detail__body">
        <p class="lr42-detail__meta">
          <span class="lr42-detail__time">{{
            formatTime(performance.startsAt, payload, strings)
          }}</span>
          <span v-if="running" class="lr42-detail__badge">{{ strings.inProgress }}</span>
          <span v-if="statusLabel" class="lr42-detail__badge lr42-detail__badge--alert">
            {{ statusLabel }}
          </span>
          <span v-if="performance.soldOut" class="lr42-detail__badge">{{ strings.soldOut }}</span>
          <span v-if="showLocation" class="lr42-detail__location">{{
            performance.location.name
          }}</span>
        </p>

        <h1 class="lr42-detail__title">{{ performance.production.title }}</h1>
        <p v-if="performance.production.subtitle" class="lr42-detail__subtitle">
          {{ performance.production.subtitle }}
        </p>
        <p v-if="performance.production.author" class="lr42-detail__author">
          {{ performance.production.author }}
        </p>

        <p class="lr42-detail__facts">
          <span v-if="performance.doorsOpenAt">
            {{ strings.doorsOpen }} {{ formatTime(performance.doorsOpenAt, payload, strings) }}
          </span>
          <span v-if="runtimeLabel">{{ runtimeLabel }}</span>
          <span v-if="intermissionLabel">{{ intermissionLabel }}</span>
        </p>

        <p v-if="surtitles.length > 0" class="lr42-detail__surtitles" :title="surtitleTitle">
          <span class="lr42-detail__surtitles-label">{{ strings.surtitlesLabel }}</span>
          <span v-for="language in surtitles" :key="language.code" class="lr42-detail__surtitle">{{
            language.label
          }}</span>
        </p>

        <ul v-if="cast.length > 0" class="lr42-detail__cast">
          <li v-for="member in cast" :key="`${member.name}-${member.roleName ?? ''}`">
            <span v-if="member.roleName" class="lr42-detail__role">{{ member.roleName }}</span>
            <span>{{ member.name }}</span>
          </li>
        </ul>

        <div v-if="sponsors.length > 0" class="lr42-detail__sponsors">
          <template v-for="sponsor in sponsors" :key="sponsor.id">
            <img v-if="logo(sponsor.id)" :src="logo(sponsor.id)" :alt="sponsor.name" />
            <span v-else>{{ sponsor.name }}</span>
          </template>
        </div>

        <div v-if="codes.length > 0" class="lr42-detail__codes">
          <QrCode
            v-for="code in codes"
            :key="code.key"
            :value="code.value"
            :caption="code.caption"
          />
        </div>
      </div>
    </template>

    <p v-else class="lr42-detail__empty">{{ strings.noPerformance }}</p>
  </div>
</template>

<style scoped>
.lr42-detail {
  width: 100%;
  height: 100%;
  display: flex;
  gap: var(--lr42-gap, 3%);
  padding: var(--lr42-padding, 4%);
  box-sizing: border-box;
  background: var(--lr42-bg, transparent);
  color: var(--lr42-fg, #f6eedc);
  font-family: var(--lr42-font, Georgia, 'Times New Roman', serif);
  overflow: hidden;
}
.lr42-detail--poster {
  padding: 0;
  position: relative;
}
.lr42-detail--text {
  align-items: center;
  justify-content: center;
  text-align: center;
}
.lr42-detail__poster {
  flex: 0 0 40%;
  min-width: 0;
  overflow: hidden;
}
.lr42-detail--poster .lr42-detail__poster {
  position: absolute;
  inset: 0;
  flex: none;
}
.lr42-detail__poster img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.lr42-detail__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--lr42-gap, 1.5%);
  justify-content: center;
}
.lr42-detail--poster .lr42-detail__body {
  position: relative;
  justify-content: flex-end;
  padding: 5%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.82) 0%, rgba(0, 0, 0, 0) 65%);
}
.lr42-detail__meta,
.lr42-detail__facts {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.8em;
  margin: 0;
  font-family: var(--lr42-meta-font, var(--lr42-font, inherit));
  font-size: var(--lr42-meta-size, clamp(12px, 1.6vw, 26px));
  color: var(--lr42-muted, rgba(246, 238, 220, 0.68));
}
.lr42-detail__time {
  font-size: var(--lr42-time-size, clamp(18px, 2.6vw, 44px));
  color: var(--lr42-accent, #e2b44e);
  font-variant-numeric: tabular-nums;
}
.lr42-detail__badge {
  padding: 0.15em 0.6em;
  border: 1px solid currentColor;
  border-radius: var(--lr42-radius, 2px);
  font-size: 0.8em;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.lr42-detail__badge--alert {
  color: var(--lr42-cancelled, #f5b8a0);
}
.lr42-detail__title {
  margin: 0;
  font-family: var(--lr42-title-font, var(--lr42-font, inherit));
  font-size: var(--lr42-title-size, clamp(28px, 5.5vw, 108px));
  font-weight: var(--lr42-title-weight, 400);
  line-height: 1.05;
  letter-spacing: -0.01em;
}
.is-cancelled .lr42-detail__title {
  text-decoration: line-through;
  text-decoration-thickness: 0.05em;
}
.lr42-detail__subtitle {
  margin: 0;
  font-size: var(--lr42-subtitle-size, clamp(16px, 2.4vw, 44px));
  color: var(--lr42-muted, rgba(246, 238, 220, 0.75));
}
.lr42-detail__author {
  margin: 0;
  font-size: var(--lr42-body-size, clamp(14px, 1.8vw, 30px));
  font-style: italic;
}
.lr42-detail__cast {
  margin: 0;
  padding: 0;
  list-style: none;
  columns: 2;
  font-size: var(--lr42-body-size, clamp(12px, 1.5vw, 24px));
}
.lr42-detail__cast li {
  display: flex;
  gap: 0.5em;
  break-inside: avoid;
}
.lr42-detail__role {
  color: var(--lr42-muted, rgba(246, 238, 220, 0.6));
}
.lr42-detail__location {
  font-size: 0.95em;
}
.lr42-detail__surtitles {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5em;
  margin: 0;
  font-family: var(--lr42-meta-font, var(--lr42-font, inherit));
  font-size: var(--lr42-meta-size, clamp(12px, 1.6vw, 26px));
  color: var(--lr42-muted, rgba(246, 238, 220, 0.68));
}
.lr42-detail__surtitles-label {
  font-size: 0.8em;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.lr42-detail__surtitle {
  padding: 0.15em 0.6em;
  border: 1px solid currentColor;
  border-radius: var(--lr42-radius, 2px);
  font-size: 0.8em;
  color: var(--lr42-accent, #e2b44e);
}
.lr42-detail__codes {
  display: flex;
  align-items: flex-end;
  gap: 1.2em;
}
.lr42-detail__sponsors {
  display: flex;
  align-items: center;
  gap: 1.5em;
  margin-top: auto;
}
.lr42-detail__sponsors img {
  max-height: var(--lr42-sponsor-height, 6vh);
  max-width: 20%;
  object-fit: contain;
}
.lr42-detail__empty {
  margin: auto;
  font-size: var(--lr42-subtitle-size, clamp(16px, 2.4vw, 44px));
  color: var(--lr42-muted, rgba(246, 238, 220, 0.45));
  font-style: italic;
}
</style>
